import React from 'react';
import { Mail } from 'lucide-react';
import { LeadUnreadBadge } from './LeadUnreadBadge.jsx';
import { formatDateTime } from '../utils.js';

const formatDateOnly = (value) => {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return value; }
};

function InstagramSmallIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function TiktokSmallIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.87a8.16 8.16 0 004.76 1.52V6.94a4.84 4.84 0 01-1-.25z"/>
    </svg>
  );
}

export const LeadCard = ({
    lead,
    status,
    isActive,
    isDragging,
    isBusy,
    showOwner = false,
    onSelect,
    onDragStart,
    onDragEnd,
    unreadCount = 0,
    statusLookup,
}) => {
    const handleDragStart = (event) => {
        event.dataTransfer.setData('text/plain', lead.id);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart(lead);
    };

    const statusInfo = statusLookup?.get(lead.status);
    const subStatusLabel = statusInfo?.label || '';
    const statusBadgeClass = lead.status ? `status-badge status-badge--${lead.status}` : 'status-badge status-badge--default';

    const daysSince = (ts) => {
      if (!ts) return null;
      return Math.floor((Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60 * 24));
    };
    const contactDays = daysSince(lead.lastOutgoingMessageAt);

    const cardClassName = [
        'lead-card',
        isActive ? 'lead-card--active' : '',
        isDragging ? 'lead-card--dragging' : '',
        isBusy ? 'lead-card--busy' : '',
        status?.id === 'manager_response_needed' ? 'lead-card--attention' : ''
    ].filter(Boolean).join(' ');

    return (
        <article
            className={cardClassName}
            draggable
            onDragStart={handleDragStart}
            onDragEnd={onDragEnd}
            onClick={() => onSelect(lead.id)}
        >
            <div className='lead-card__row lead-card__row--top'>
                <span className='lead-card__name'>{lead.name}</span>
                <div className='lead-card__indicators'>
                    <LeadUnreadBadge count={unreadCount} />
                </div>
            </div>

            {(lead.instagram || lead.email || lead.tiktok) && (
                <div className='lead-card__social-row'>
                    {lead.instagram && (
                        <a
                            className='lead-card__social-link'
                            href={`https://www.instagram.com/${lead.instagram.replace(/^@/, '')}/`}
                            target='_blank'
                            rel='noopener noreferrer'
                            title={`@${lead.instagram.replace(/^@/, '')}`}
                            onClick={e => e.stopPropagation()}
                        >
                            <InstagramSmallIcon />
                            <span>@{lead.instagram.replace(/^@/, '')}</span>
                        </a>
                    )}
                    {lead.email && (
                        <a
                            className='lead-card__social-link'
                            href={`mailto:${lead.email}`}
                            title={lead.email}
                            onClick={e => e.stopPropagation()}
                        >
                            <Mail size={12} strokeWidth={1.8} />
                            <span>{lead.email.length > 18 ? lead.email.slice(0, 14) + '…' : lead.email}</span>
                        </a>
                    )}
                    {lead.tiktok && (
                        <span className='lead-card__social-link' title={lead.tiktok}>
                            <TiktokSmallIcon />
                            <span>{lead.tiktok}</span>
                        </span>
                    )}
                </div>
            )}

            <div className='lead-card__meta'>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="2" width="14" height="13" rx="2" />
                    <path d="M1 6h14" />
                    <path d="M5 1v2M11 1v2" />
                </svg>
                {formatDateOnly(lead.createdAt)}
            </div>

            {contactDays !== null && (
                <div className='lead-card__last-contact'>
                    {contactDays === 0 ? 'Сегодня' : `${contactDays} дн. назад`}
                </div>
            )}

            {subStatusLabel && (
                <span className={statusBadgeClass}>{subStatusLabel}</span>
            )}
        </article>
    );
};
